import { useCallback, useState } from 'react';
import {
  Inline,
  InlineItem,
  H3,
  Stack,
  Text,
  Secondary,
  UtilityButton,
  Tooltip,
} from '@kinesis/bungle';
import { differenceInDays, parseISO } from 'date-fns/fp';
import { noop } from 'lodash/fp';
import { formatDistanceStrings, formatDateString } from '@/date';
import { summariseScopes } from '@/scopes';
import { AccessKeyDeleteConfirmation } from './access-key-delete-confirmation';

const STALE_DAYS = 90;
const OLD_KEY_DAYS = 365;

const AccessKeyItem = (props: AccessKeyItemProps) => {
  const {
    id,
    name,
    created,
    lastUsed,
    expiresAt,
    scopes = ['*'],
    now,
    onClick,
    onDelete = noop,
  } = props;
  const distance = formatDistanceStrings(now, created);
  const date = formatDateString(created);
  const lastUsedDistance = lastUsed
    ? formatDistanceStrings(now, lastUsed)
    : null;
  const lastUsedDate = lastUsed ? formatDateString(lastUsed) : null;
  const isStale =
    lastUsed == null ||
    differenceInDays(parseISO(lastUsed), parseISO(now)) >= STALE_DAYS;
  const isExpired = expiresAt != null && parseISO(expiresAt) <= parseISO(now);
  const expiresAtDistance = expiresAt
    ? formatDistanceStrings(now, expiresAt)
    : null;
  const expiresAtDate = expiresAt ? formatDateString(expiresAt) : null;
  const isOldWithoutExpiration =
    expiresAt == null &&
    differenceInDays(parseISO(created), parseISO(now)) >= OLD_KEY_DAYS;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const onDeleteKey = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);
  const onShowConfirmDeleteKey = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, [setShowDeleteConfirmation]);
  const onCloseConfirmDeleteKey = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, [setShowDeleteConfirmation]);
  const onClickItem = useCallback(() => {
    onClick?.(id);
  }, [id, onClick]);

  const label = name ?? `Key ${id.slice(0, 8)}`;

  return (
    <Inline>
      <InlineItem sizing='fill-container'>
        <Inline space='small'>
          <InlineItem sizing='fill-container'>
            <Stack space='xsmall'>
              <H3 noWrap>
                <span
                  onClick={onClickItem}
                  style={{
                    cursor: onClick ? 'pointer' : undefined,
                    ...(isExpired ? { opacity: 0.5 } : undefined),
                  }}
                >
                  {label}
                </span>
              </H3>
              <Text>
                <Secondary>
                  Created{' '}
                  <Tooltip title={date} placement='top'>
                    <span>{distance}</span>
                  </Tooltip>
                  {' · '}
                  {lastUsedDistance ? (
                    <>
                      Last used{' '}
                      <Tooltip title={lastUsedDate!} placement='top'>
                        <span
                          style={
                            isStale
                              ? { color: 'var(--color-text-warning, #b45309)' }
                              : undefined
                          }
                        >
                          {lastUsedDistance}
                        </span>
                      </Tooltip>
                    </>
                  ) : (
                    <span
                      style={{ color: 'var(--color-text-warning, #b45309)' }}
                    >
                      Never used
                    </span>
                  )}
                  {expiresAt != null && (
                    <>
                      {' · '}
                      {isExpired ? (
                        <span
                          style={{ color: 'var(--color-text-danger, #dc2626)' }}
                        >
                          Expired
                        </span>
                      ) : (
                        <Tooltip title={expiresAtDate!} placement='top'>
                          <span>Expires {expiresAtDistance}</span>
                        </Tooltip>
                      )}
                    </>
                  )}
                  {isOldWithoutExpiration && (
                    <>
                      {' · '}
                      <span
                        style={{ color: 'var(--color-text-warning, #b45309)' }}
                      >
                        No expiration set
                      </span>
                    </>
                  )}
                </Secondary>
              </Text>
            </Stack>
          </InlineItem>
          <InlineItem>
            <Text>
              <Secondary>{summariseScopes(scopes)}</Secondary>
            </Text>
          </InlineItem>
        </Inline>
      </InlineItem>
      <InlineItem>
        {showDeleteConfirmation && (
          <AccessKeyDeleteConfirmation
            onDeleteKey={onDeleteKey}
            onClose={onCloseConfirmDeleteKey}
          />
        )}
        <Tooltip title='Delete key' placement='top'>
          <UtilityButton
            icon='delete'
            magnitude='small'
            variant='danger'
            onClick={onShowConfirmDeleteKey}
          />
        </Tooltip>
      </InlineItem>
    </Inline>
  );
};

export type AccessKeyItemProps = {
  id: string;
  name?: string;
  created: string;
  lastUsed?: string | null;
  expiresAt?: string | null;
  scopes?: string[];
  now: string;
  onClick?: (id: string) => void;
  onDelete?: (id: string) => void;
};

export { AccessKeyItem };
